import { DataView } from "@/features/common/components/dataview/DataView";
import { GalleryItem, GalleryItemProperty } from "@/features/common/components/dataview/GalleryItem";
import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import { ListItem } from "@/features/common/components/dataview/ListItem";
import { HeaderBar } from "@/features/common/components/HeaderBar";
import { HeaderBarSelector } from "@/features/common/components/HeaderBarSelector";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";
import { SubscriptionGate } from "@/features/common/components/SubscriptionGate";
import { useEvItems, type EvItemResponseModel } from "@/features/ev/api/useEvItems";
import { EvSmallProductDisplay } from "@/features/ev/components/EvSmallProductDisplay";
import { EvListSortOptions } from "@/features/ev/EvListSort";
import { EvProductTypeOptions } from "@/features/ev/EvProductType";
import { useTitle } from "@/lib/common/useTitle";
import { useUserPreferencesState, type UserPreferencesState } from "@/lib/state/UserPreferencesState";
import { BoxIcon, SortAscIcon } from "lucide-react";
import { useCallback, useMemo, useRef, useState, type FC } from "react";
import { SplitScreen, SplitScreenLeftPanel, SplitScreenRightPanel } from "@/features/common/components/SplitScreen";
import { EvItemBreakdown } from "@/features/ev/components/EvItemBreakdown";
import { EvCategoryVariants } from "@/features/ev/components/EvCategoryVariants";
import type { EvItemBreakdownResponseModelCategory } from "@/features/ev/api/useEvItemBreakdown";
import type { EvItemBreakdownCategoryVariantResponseModel } from "@/features/ev/api/useEvItemBreakdownCategoryVariants";
import { ProductView } from "@/features/products/components/ProductView";

export const PageEvItems: FC = () => {
	useTitle("EV Tracker");

	const breadcrumb = useMemo(() => [
		{ label: "Dashboard", href: "/dashboard" },
		{ label: "EV Tracker" }
	], []);

	const { state, dispatchSetEvProductType, dispatchSetEvListSort } = useUserPreferencesState();

	return (
		<>
			<HeaderBar title="EV Tracker" description="Track the estimated value of various Flesh and Blood sets" breadcrumb={breadcrumb} />
			<HeaderBarTools>
				<HeaderBarSelector
					value={state.evProductType}
					onChange={dispatchSetEvProductType}
					placeholder="Product Type"
					icon={BoxIcon}
					options={EvProductTypeOptions} />

				<HeaderBarSelector
					value={state.evListSort}
					onChange={dispatchSetEvListSort}
					placeholder="Sort"
					icon={SortAscIcon}
					options={EvListSortOptions} />
			</HeaderBarTools>

			<SubscriptionGate featureName="EV Tracker">
				<PageEvItemsInternal state={state} />
			</SubscriptionGate>
		</>
	);
};

interface PageEvItemsInternalProps {
	state: UserPreferencesState
}

const PageEvItemsInternal: FC<PageEvItemsInternalProps> = props => {
	const {
		state
	} = props;

	const evItems = useEvItems(state.evListSort, state.evProductType);
	const [selectedEvItem, setSelectedEvItem] = useState<EvItemResponseModel | null>(null);
	const [selectedCategory, setSelectedCategory] = useState<EvItemBreakdownResponseModelCategory | null>(null);
	const [selectedCategoryVariant, setSelectedCategoryVariant] = useState<EvItemBreakdownCategoryVariantResponseModel | null>(null);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const rightPanelRef = useRef<HTMLDivElement | null>(null);

	const onSelectEvItem = useCallback((evItem: EvItemResponseModel | null) => {
		setSelectedEvItem(evItem);
		setSelectedCategory(null);
		setSelectedCategoryVariant(null);
	}, []);

	const onSelectCategory = useCallback((category: EvItemBreakdownResponseModelCategory) => {
		setSelectedCategory(category);
	}, []);

	const onSelectCategoryVariant = useCallback((categoryVariant: EvItemBreakdownCategoryVariantResponseModel) => {
		setSelectedCategoryVariant(categoryVariant);
	}, []);

	const onBack = useCallback(() => {
		if (selectedCategoryVariant) {
			setSelectedCategoryVariant(null);
		} else {
			setSelectedCategory(null);
		}
	}, [selectedCategoryVariant]);

	const additionalProps = useMemo(() => ({
		onSelectEvItem
	}), [onSelectEvItem]);

	return (
		<SplitScreen fixed visible={!!selectedEvItem} onVisibleChange={visible => !visible && onSelectEvItem(null)}>
			<SplitScreenLeftPanel ref={scrollRef}>
				<DataView
					forQuery={evItems}
					scrollRef={scrollRef}
					getKey={x => x.productId}
					listViewHeaderLabels={["Product", "Box Market Value", "Current Estimated Value", "Difference"]}
					listViewHeaderClassNames={["min-w-0", "w-44 @2xl:w-48", "w-48", "w-52 @2xl:w-64"]}
					listComponent={PageEvItemsDataViewListItem}
					galleryComponent={PageEvItemsDataViewGalleryItem}
					additionalProps={additionalProps}
					galleryMinWidth={208} />
			</SplitScreenLeftPanel>
			<SplitScreenRightPanel ref={rightPanelRef} onBack={selectedCategory ? onBack : undefined}>
				{
					selectedEvItem && !selectedCategory &&
					<EvItemBreakdown
						evSetType={selectedEvItem.setType}
						evProductType={selectedEvItem.productType}
						onSelectCategory={onSelectCategory} />
				}

				{
					selectedEvItem && selectedCategory && !selectedCategoryVariant &&
					<EvCategoryVariants
						evSetType={selectedEvItem.setType}
						category={selectedCategory.category}
						categoryName={selectedCategory.categoryName}
						scrollRef={rightPanelRef}
						onSelectCategoryVariant={onSelectCategoryVariant} />
				}

				{
					selectedCategoryVariant &&
					<ProductView className="min-h-full" productId={selectedCategoryVariant.productId} />
				}
			</SplitScreenRightPanel>
		</SplitScreen>
	);
};

interface PageEvItemsDataViewProps extends EvItemResponseModel {
	onSelectEvItem: (evItem: EvItemResponseModel) => void;
};

const PageEvItemsDataViewListItem: FC<PageEvItemsDataViewProps> = props => {
	const {
		marketPrice,
		currentEstimatedValue,
		marketPriceDelta,
		onSelectEvItem
	} = props;

	const onClick = useCallback(() => onSelectEvItem(props), [onSelectEvItem, props]);

	return (
		<ListItem onClick={onClick}>
			<TableCell>
				<EvSmallProductDisplay {...props} />
			</TableCell>
			<TableCell><MoneyDisplay money={marketPrice} mode="Standard" /></TableCell>
			<TableCell><MoneyDisplay money={currentEstimatedValue} mode="Standard" /></TableCell>
			<TableCell><MoneyDisplay money={marketPriceDelta} mode="DeltaPercentage" originalValue={marketPrice.value} /></TableCell>
		</ListItem>
	);
};

const PageEvItemsDataViewGalleryItem: FC<PageEvItemsDataViewProps> = props => {
	const {
		productName,
		fcId,
		productImage,
		marketPrice,
		currentEstimatedValue,
		marketPriceDelta,
		onSelectEvItem
	} = props;

	const onClick = useCallback(() => onSelectEvItem(props), [onSelectEvItem, props]);

	return (
		<GalleryItem title={productName} description={fcId} imageUrl={productImage} onClick={onClick}>
			<GalleryItemProperty label="Estimated Value">
				<MoneyDisplay money={currentEstimatedValue} mode="Standard" />
			</GalleryItemProperty>
			<GalleryItemProperty label="Market Price">
				<MoneyDisplay money={marketPrice} mode="Standard" />
			</GalleryItemProperty>
			<GalleryItemProperty label="Difference">
				<MoneyDisplay money={marketPriceDelta} mode="DeltaPercentage" originalValue={marketPrice.value} />
			</GalleryItemProperty>
		</GalleryItem>
	);
};
